import { Avatar, Box, Drawer, IconButton, Menu, MenuItem } from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import {
  AccountCircle,
  Close,
  HomeOutlined,
  PersonOutline,
  MenuBookOutlined,
  QuizOutlined,
  AssignmentOutlined,
  PhoneOutlined,
  LoginOutlined,
  LogoutOutlined,
  DashboardOutlined,
  KeyboardArrowDown,
  QuizTwoTone,
} from "@mui/icons-material";

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Scrollspy from "react-scrollspy";
import useAuthContext from "../context/AuthContext";

import { createIdentityAPIEndpoint, EndPoints } from "../services/AuthServices";

import ContactUs from "./ContactUs";

function NavBar() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [activeLink, setActiveLink] = useState("home");
  const [userDetail, setUserDetail] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated, logout } = useAuthContext();

  const isHomePage = location.pathname === "/";

  /* =========================================
     MENU
  ========================================= */

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /* =========================================
     DRAWER
  ========================================= */

  const closeDrawer = () => {
    setIsOpen(false);
  };

  const toggleDrawer = () => {
    setIsOpen((prev) => !prev);
  };

  /* =========================================
     LOGOUT
  ========================================= */

  const handleLogout = () => {
    logout();
    handleMenuClose();
    closeDrawer();
    navigate("/login");
  };

  /* =========================================
     SCROLL TO SECTION
  ========================================= */

  const handleScrollToSection = (id) => {
    closeDrawer();

    if (!isHomePage) {
      navigate("/");

      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: "smooth",
        });
      }, 150);

      return;
    }

    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
    });
  };

  /* =========================================
     USER DETAILS + SCROLL
  ========================================= */

  useEffect(() => {
    createIdentityAPIEndpoint(EndPoints.details)
      .fetch()
      .then((res) => {
        setUserDetail(res.data);
      })
      .catch((err) => {
        console.log(err);
      });

    const handleScroll = () => {
      setSticky(window.scrollY > 35);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* =========================================
     ACTIVE ROUTE
  ========================================= */

  useEffect(() => {
    if (location.pathname === "/lessons") {
      setActiveLink("lessons");
    } else if (location.pathname === "/exams") {
      setActiveLink("exams");
    } else if (location.pathname === "/homeworks") {
      setActiveLink("homeworks");
    } else if (location.pathname === "/") {
      setActiveLink("home");
    }
  }, [location.pathname]);

  /* =========================================
     MOBILE DRAWER
  ========================================= */

  const drawerContent = (
    <div className="mobile_nav">
      {/* HEADER */}
      <div className="mobile_nav_header">
        <div className="mobile_brand">
          <div>
            <strong className="brand_text">PureEng</strong>
            <span>منصة تعليم اللغة الإنجليزية</span>
          </div>
        </div>

        <IconButton className="mobile_close" onClick={closeDrawer}>
          <Close />
        </IconButton>
      </div>

      {/* USER */}
      {isAuthenticated && (
        <div className="mobile_user">
          <Avatar className="mobile_user_avatar">
            <AccountCircle />
          </Avatar>

          <div>
            <strong>{userDetail?.fullName || "الطالب"}</strong>
            <span>حساب الطالب</span>
          </div>
        </div>
      )}

      {/* LINKS */}
      <nav className="mobile_links">
        <button
          className={`mobile_link ${activeLink === "home" ? "active" : ""}`}
          onClick={() => handleScrollToSection("home")}
        >
          <HomeOutlined />
          <span>الرئيسية</span>
        </button>
        <button
          className={`mobile_link ${activeLink === "placement-test" ? "active" : ""}`}
          onClick={() => handleScrollToSection("placement-test")}
        >
          <QuizTwoTone />
          أختبر مستواك
        </button>
        <button
          className={`mobile_link ${activeLink === "about" ? "active" : ""}`}
          onClick={() => handleScrollToSection("about")}
        >
          <PersonOutline />
          <span>عن المنصة</span>
        </button>

        <Link
          to="/lessons"
          className={`mobile_link ${activeLink === "lessons" ? "active" : ""}`}
          onClick={closeDrawer}
        >
          <MenuBookOutlined />
          <span>الدروس</span>
        </Link>

        <Link
          to="/exams"
          className={`mobile_link ${activeLink === "exams" ? "active" : ""}`}
          onClick={closeDrawer}
        >
          <QuizOutlined />
          <span>الامتحانات</span>
        </Link>

        <Link
          to="/homeworks"
          className={`mobile_link ${
            activeLink === "homeworks" ? "active" : ""
          }`}
          onClick={closeDrawer}
        >
          <AssignmentOutlined />
          <span>الواجبات</span>
        </Link>

        <button
          className={`mobile_link ${activeLink === "contact" ? "active" : ""}`}
          onClick={() => handleScrollToSection("contact")}
        >
          <PhoneOutlined />
          <span>تواصل معنا</span>
        </button>
      </nav>

      {/* BOTTOM */}
      <div className="mobile_nav_bottom">
        {!isAuthenticated ? (
          <Link to="/login" className="mobile_login_btn" onClick={closeDrawer}>
            <LoginOutlined />
            تسجيل الدخول
          </Link>
        ) : (
          <>
            <Link
              to="/my-profile"
              className="mobile_account_link"
              onClick={closeDrawer}
            >
              <PersonOutline />
              حسابي
            </Link>

            <button className="mobile_logout_btn" onClick={handleLogout}>
              <LogoutOutlined />
              تسجيل الخروج
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ContactUs />

      {/* =========================================
          DESKTOP NAVBAR
      ========================================= */}

      <motion.header
        initial={{
          y: -80,
          opacity: 0,
        }}
        animate={{
          y: 0,
          opacity: 1,
        }}
        transition={{
          duration: 0.7,
          ease: "easeOut",
        }}
        className={`nav_bar ${sticky ? "nav_sticky" : ""}`}
      >
        <div className="nav_container">
          {/* LOGO */}
          <Link
            to="/"
            className="nav_brand"
            onClick={() => setActiveLink("home")}
          >
            <div className="brand_text">
              <strong>PureEng</strong>
              <span>English Academy</span>
            </div>
          </Link>

          {/* DESKTOP LINKS */}
          <nav className="desktop_links">
            <Scrollspy
              items={[
                "home",
                "placement-test",
                "about",
                "lessons",
                "exams",
                "homeworks",
                "contact",
              ]}
              currentClassName="active"
              componentTag="ul"
              onUpdate={(el) => {
                if (el) {
                  setActiveLink(el.id);
                }
              }}
              offset={-120}
            >
              <li>
                <button
                  className={`nav_link ${
                    activeLink === "home" ? "active" : ""
                  }`}
                  onClick={() => handleScrollToSection("home")}
                >
                  الرئيسية
                </button>
              </li>
              <li>
                <button
                  className={`nav_link ${
                    activeLink === "placement-test" ? "active" : ""
                  }`}
                  onClick={() => handleScrollToSection("placement-test")}
                >
                  أختبر مستواك
                </button>
              </li>
              <li>
                <button
                  className={`nav_link ${
                    activeLink === "about" ? "active" : ""
                  }`}
                  onClick={() => handleScrollToSection("about")}
                >
                  عن المنصة
                </button>
              </li>

              <li>
                <Link
                  to="/lessons"
                  className={`nav_link ${
                    activeLink === "lessons" ? "active" : ""
                  }`}
                  onClick={() => setActiveLink("lessons")}
                >
                  الدروس
                </Link>
              </li>

              <li>
                <Link
                  to="/exams"
                  className={`nav_link ${
                    activeLink === "exams" ? "active" : ""
                  }`}
                  onClick={() => setActiveLink("exams")}
                >
                  الامتحانات
                </Link>
              </li>

              <li>
                <Link
                  to="/homeworks"
                  className={`nav_link ${
                    activeLink === "homeworks" ? "active" : ""
                  }`}
                  onClick={() => setActiveLink("homeworks")}
                >
                  الواجبات
                </Link>
              </li>

              <li>
                <button
                  className={`nav_link ${
                    activeLink === "contact" ? "active" : ""
                  }`}
                  onClick={() => handleScrollToSection("contact")}
                >
                  تواصل معنا
                </button>
              </li>
            </Scrollspy>
          </nav>

          {/* RIGHT SIDE */}
          <div className="nav_actions">
            {!isAuthenticated ? (
              <Link to="/login" className="nav_login_btn">
                <span>تسجيل الدخول</span>
                <LoginOutlined />
              </Link>
            ) : (
              <>
                <button className="user_profile_btn" onClick={handleMenuOpen}>
                  <div className="user_text">
                    <strong className="user_name">
                      {userDetail?.fullName || "الطالب"}
                    </strong>

                    <span className="user_account_text">حسابي</span>
                  </div>

                  <Avatar className="user_avatar">
                    <AccountCircle />
                  </Avatar>

                  <KeyboardArrowDown className="user_arrow" />
                </button>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  className="account_menu"
                >
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate("/my-profile");
                    }}
                  >
                    <PersonOutline />
                    الملف الشخصي
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate("/my-lessons");
                    }}
                  >
                    <MenuBookOutlined />
                    دروسي
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      navigate("/my-results");
                    }}
                  >
                    <DashboardOutlined />
                    نتائجي
                  </MenuItem>

                  <MenuItem onClick={handleLogout}>
                    <LogoutOutlined />
                    تسجيل الخروج
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>

          {/* MOBILE MENU */}
          <IconButton
            className="mobile_menu_button"
            onClick={toggleDrawer}
            aria-label="فتح القائمة"
          >
            <MenuIcon />
          </IconButton>
        </div>
      </motion.header>

      {/* MOBILE DRAWER */}

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={closeDrawer}
        className="mobile_drawer"
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}

export default NavBar;
